import type { Element } from '../engine/element';
import { Battery } from '../engine/elements';
import { SPEC_BY_TYPE } from '../engine/catalog';
import { fmt } from '../engine/format';
import { Port } from '../engine/subckt';

export type ChannelKind = 'V' | 'I' | 'P' | 'T' | 'SoC';

export const KIND_LABEL: Record<ChannelKind, string> = {
  V: '전압', I: '전류', P: '전력', T: '온도', SoC: '잔량',
};
const KIND_UNIT: Record<ChannelKind, string> = { V: 'V', I: 'A', P: 'W', T: '℃', SoC: '%' };

const COLORS = ['#60a5fa', '#fbbf24', '#f472b6', '#34d399', '#a78bfa', '#fb923c'];

/** 이 소자에서 볼 수 있는 값들 */
export function channelKindsFor(el: Element): ChannelKind[] {
  if (el.type === 'wire' || el.type === 'ground' || el.type === 'subckt') return [];
  const kinds: ChannelKind[] = ['V', 'I', 'P'];
  if (el.thermalSpec()) kinds.push('T');
  if (el instanceof Battery && (el.num('capacity', 0) > 0 || el.isLithium)) kinds.push('SoC');
  return kinds;
}

function readValue(el: Element, kind: ChannelKind): number {
  switch (kind) {
    case 'V':
      if (el instanceof Battery) return el.terminalVoltage;
      if (el instanceof Port) return el.voltage;
      return el.voltageDrop;
    case 'I': return el.current;
    case 'P': return el.power;
    case 'T': return el.temp;
    case 'SoC': return el instanceof Battery ? el.soc * 100 : 0;
  }
}

interface Channel {
  el: Element;
  kind: ChannelKind;
  color: string;
  label: string;
  pinned: boolean;
}

const MAX_POINTS = 24000;

/**
 * 오실로스코프: 여러 소자의 값을 시간에 따라 기록하고 그린다.
 * 📈 로 고정한 채널이 없으면 지금 선택한 소자의 전압·전류를 미리보기로 보여준다.
 */
export class Scope {
  private channels: Channel[] = [];
  private t: number[] = [];
  private data: number[][] = [];
  windowSec = 5;
  private previewEl: Element | null = null;

  constructor(
    private canvas: HTMLCanvasElement,
    private legend: HTMLElement,
  ) {}

  private labelFor(el: Element, kind: ChannelKind): string {
    const spec = SPEC_BY_TYPE[el.type];
    const name = el instanceof Port ? `포트 ${el.portName}` : (spec?.label ?? el.type);
    return `${name} #${el.id.replace(/\D/g, '')} ${KIND_LABEL[kind]}`;
  }

  get pinnedCount(): number {
    return this.channels.filter((c) => c.pinned).length;
  }

  add(el: Element, kind: ChannelKind): void {
    if (this.channels.some((c) => c.pinned && c.el === el && c.kind === kind)) return;
    const pinned = this.channels.filter((c) => c.pinned);
    const used = new Set(pinned.map((c) => c.color));
    const color = COLORS.find((c) => !used.has(c)) ?? COLORS[pinned.length % COLORS.length];
    this.channels = [...pinned, { el, kind, color, label: this.labelFor(el, kind), pinned: true }];
    this.clearData();
  }

  remove(index: number): void {
    this.channels.splice(index, 1);
    if (this.pinnedCount === 0) this.setPreview(this.previewEl, true);
    this.clearData();
  }

  /** 선택이 바뀔 때 — 고정 채널이 없으면 미리보기 교체 */
  setPreview(el: Element | null, force = false): void {
    if (!force && el === this.previewEl) return;
    this.previewEl = el;
    if (this.pinnedCount > 0) return;
    this.channels = el && channelKindsFor(el).length
      ? (['V', 'I'] as ChannelKind[]).map((kind, i) => ({
        el, kind, color: COLORS[i], label: this.labelFor(el, kind), pinned: false,
      }))
      : [];
    this.clearData();
  }

  /** 회로를 새로 불러왔을 때 */
  reset(): void {
    this.channels = [];
    this.previewEl = null;
    this.clearData();
  }

  /** 없어진 소자의 채널 정리 */
  prune(alive: Set<Element>): void {
    const before = this.channels.length;
    this.channels = this.channels.filter((c) => alive.has(c.el));
    if (this.channels.length !== before) this.clearData();
  }

  clearData(): void {
    this.t = [];
    this.data = this.channels.map(() => []);
    this.renderLegend();
  }

  sample(time: number): void {
    if (this.channels.length === 0) return;
    if (this.t.length && time <= this.t[this.t.length - 1]) {
      if (time < this.t[this.t.length - 1]) this.clearData(); // 초기화로 시간이 되감김
      else return;
    }
    this.t.push(time);
    this.channels.forEach((c, i) => this.data[i].push(readValue(c.el, c.kind)));
    if (this.t.length > MAX_POINTS) {
      const cut = MAX_POINTS / 4;
      this.t.splice(0, cut);
      for (const d of this.data) d.splice(0, cut);
    }
  }

  exportCsv(): string {
    const head = ['time_s', ...this.channels.map((c) => `${c.label} (${KIND_UNIT[c.kind]})`)];
    const rows = this.t.map((t, i) => [t.toPrecision(8), ...this.data.map((d) => d[i].toPrecision(6))].join(','));
    return '﻿' + [head.map((h) => `"${h.replace(/"/g, '""')}"`).join(','), ...rows].join('\n');
  }

  downloadCsv(): void {
    if (!this.t.length) return;
    const blob = new Blob([this.exportCsv()], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `circuitlab-scope-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  private renderLegend(): void {
    this.legend.innerHTML = '';
    if (this.channels.length === 0) {
      this.legend.textContent = '소자를 선택하면 전압·전류 파형이 보여요. 📈 로 여러 개를 고정할 수 있어요';
      return;
    }
    this.channels.forEach((c, i) => {
      const row = document.createElement('div');
      row.className = 'scope-ch';
      row.innerHTML = `<span class="sw" style="background:${c.color}"></span><span class="nm"></span><span class="val mono"></span>`;
      (row.querySelector('.nm') as HTMLElement).textContent = c.pinned ? c.label : `${c.label} (미리보기)`;
      if (c.pinned) {
        const x = document.createElement('button');
        x.textContent = '×';
        x.title = '채널 빼기';
        x.onclick = () => this.remove(i);
        row.appendChild(x);
      }
      this.legend.appendChild(row);
    });
  }

  draw(): void {
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const r = this.canvas.getBoundingClientRect();
    const W = Math.max(1, Math.round(r.width * dpr));
    const H = Math.max(1, Math.round(r.height * dpr));
    if (this.canvas.width !== W || this.canvas.height !== H) {
      this.canvas.width = W;
      this.canvas.height = H;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const w = r.width;
    const h = r.height;
    ctx.clearRect(0, 0, w, h);
    const n = this.t.length;

    // 값 표시 (범례 옆 현재값)
    const vals = this.legend.querySelectorAll<HTMLElement>('.scope-ch .val');
    this.channels.forEach((c, i) => {
      const v = this.data[i]?.[n - 1];
      if (vals[i]) vals[i].textContent = v === undefined ? '' : c.kind === 'T' ? `${v.toFixed(1)}℃` : c.kind === 'SoC' ? `${v.toFixed(1)}%` : fmt(v, KIND_UNIT[c.kind]);
    });
    if (n < 2) return;

    const tEnd = this.t[n - 1];
    const tStart = this.windowSec > 0 ? Math.max(this.t[0], tEnd - this.windowSec) : this.t[0];
    const span = Math.max(tEnd - tStart, 1e-9);
    let first = 0;
    while (first < n - 1 && this.t[first] < tStart) first++;

    // 격자 + 시간 눈금
    ctx.strokeStyle = 'rgba(148,163,184,0.12)';
    ctx.lineWidth = 1;
    ctx.fillStyle = 'rgba(148,163,184,0.55)';
    ctx.font = '9px ui-monospace, monospace';
    for (let k = 0; k <= 4; k++) {
      const x = (k / 4) * w;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    ctx.textAlign = 'left';
    ctx.fillText(fmt(tStart, 's'), 3, h - 3);
    ctx.textAlign = 'right';
    ctx.fillText(fmt(tEnd, 's'), w - 3, h - 3);

    const pad = 6;
    this.channels.forEach((c, i) => {
      const d = this.data[i];
      let lo = Infinity;
      let hi = -Infinity;
      for (let k = first; k < n; k++) {
        lo = Math.min(lo, d[k]);
        hi = Math.max(hi, d[k]);
      }
      if (!Number.isFinite(lo)) return;
      if (lo > 0 && c.kind !== 'T') lo = 0;
      if (hi < 0) hi = 0;
      if (hi - lo < 1e-12) {
        hi += 1e-6 + Math.abs(hi) * 0.1;
        lo -= 1e-6 + Math.abs(lo) * 0.1;
      }
      const y = (v: number) => h - pad - ((v - lo) / (hi - lo)) * (h - pad * 2 - 10);
      ctx.strokeStyle = c.color;
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      // 픽셀당 한 점 정도로 줄여 그리기
      const stride = Math.max(1, Math.floor((n - first) / (w * 1.5)));
      for (let k = first; k < n; k += stride) {
        const px = ((this.t[k] - tStart) / span) * w;
        if (k === first) ctx.moveTo(px, y(d[k]));
        else ctx.lineTo(px, y(d[k]));
      }
      ctx.stroke();
    });
  }
}
